import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useIntl } from 'react-intl';

import { fg } from '@atlaskit/platform-feature-flags';
import { RequestStatus, useUCSuggestions } from '@atlassian/navigation-bff-data-provider';

import { useMenuStateContext } from '../../../../context';
import {
	useSuggestionExcludeAtis,
	useSuggestionNativeAtis,
	useSuggestionPreferredAtis,
} from '../../../../hooks';

import { useFetchUser } from './useFetchUser';
import { usePrefetchLoom } from './usePrefetchLoom';

interface usePrefetchV1Return {
	triggerPrefetch: () => Promise<void>;
}

/**
 * V1 of prefetch that will try to call the backend endpoint to get list of menu items
 * Loom is NOT included. TODO: add loom preload separately https://hello.jira.atlassian.cloud/browse/NAVX-2470
 * User is conditionally fetched based on return items from the backend
 *
 * @returns Object containing triggerPrefetch function
 */
export function usePrefetchV1(): usePrefetchV1Return {
	const { product, suggestionConfig, subProduct } = useMenuStateContext();

	const isV1Disabled = fg('navx_uc_disable_v1_fetch');

	const isPrefetchEnabled = suggestionConfig.enabled && suggestionConfig.prefetch === true;
	const { locale } = useIntl();
	const onResultChange = suggestionConfig.enabled ? suggestionConfig?.onResultChange : undefined;

	const { triggerPrefetch: triggerPrefetchLoom } = usePrefetchLoom();

	// Use the shared hooks for ATIs logic
	const nativeAtis = useSuggestionNativeAtis();
	const preferredAtis = useSuggestionPreferredAtis();
	const excludeAtis = useSuggestionExcludeAtis();

	/**
	 * Fetching V1 version of menu items
	 */
	const { status, data, isLoading, hasError, fetchUCSuggestions } = useUCSuggestions({
		cloudId: suggestionConfig.enabled ? suggestionConfig.cloudId : '',
		orgId: suggestionConfig.enabled ? suggestionConfig.orgId : '',
		userId: suggestionConfig.enabled ? suggestionConfig.accountId : '',
		product,
		locale,
		subProduct: suggestionConfig.enabled ? subProduct : undefined,
		nativeAtis,
		preferredAtis,
		excludeAtis,
	});

	const prefetchTriggered = useRef(false);
	const userFetchTriggered = useRef(false);

	const shouldFetchSuggestions = !isV1Disabled && isPrefetchEnabled && suggestionConfig.enabled;

	const { fetchUserIfNeeded } = useFetchUser();

	const triggerPrefetch = useCallback(async () => {
		if (isV1Disabled) {
			return;
		}

		if (prefetchTriggered.current) {
			return;
		}

		prefetchTriggered.current = true;

		if (shouldFetchSuggestions) {
			fetchUCSuggestions();
		}

		triggerPrefetchLoom();
	}, [isV1Disabled, triggerPrefetchLoom, fetchUCSuggestions, shouldFetchSuggestions]);

	const bffHasInitialized = useMemo(() => {
		return status !== RequestStatus.INIT;
	}, [status]);

	useEffect(
		function notifySuggestionResultChange() {
			if (!shouldFetchSuggestions) {
				return;
			}

			if (onResultChange && bffHasInitialized) {
				onResultChange?.({ data, isLoading, hasError });
			}
		},
		[onResultChange, data, isLoading, hasError, bffHasInitialized, shouldFetchSuggestions],
	);

	// Reactively fetch user data when items become available
	useEffect(
		function fetchUserWhenDataReady() {
			if (!shouldFetchSuggestions || !data?.items || userFetchTriggered.current) {
				return;
			}

			userFetchTriggered.current = true;
			fetchUserIfNeeded({ items: data.items });
		},
		[shouldFetchSuggestions, data?.items, fetchUserIfNeeded],
	);

	return {
		triggerPrefetch,
	};
}
